// Monthly P&L (requirement 19) — the actual deliverable Stage 4 was missing.
//
// Gross profit = net service revenue (SERVICE_CHARGE + MANAGEMENT_FEE +
// OTHER_REVENUE) minus operating expenses minus direct cost variance. For
// most clients (ARSL/ATL/UD/etc.) salary/EOBI/PESSI are billed as separate
// pass-through recovery lines excluded from revenue, so they cancel out by
// construction and directCostVariance is 0 for them. Some clients (CBRE) bill
// a single lump invoice amount with no recovery split — their full billing
// lands in netServiceRevenue as-is, so finance records what was actually paid
// out (salary/EOBI/PESSI/other) via clientDirectCostService, and that total is
// deducted here as directCostVariance so it isn't mistaken for margin.
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { sumRevenueLines, sumLinesByCategory } from "./lineCategories";
import { sumGrossBilling, calculateGrossProfit, calculateOperatingProfit, calculateMarginPercent } from "./pnlCalculator";
import { getOperatingExpensesForPeriod } from "./operatingExpenseService";
import { getClientProfitabilityForPeriod } from "./clientProfitabilityService";
import { getDirectCostVarianceForPeriod } from "./clientDirectCostService";
import { getGstPayableSummary } from "./gstPayableService";
import { activeSnapshotDocs } from "./voidedInvoice";

const SNAPSHOTS = "approved_invoice_snapshots";

export function previousPeriod(accountingPeriod) {
  const [year, month] = accountingPeriod.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 2, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

// Percent change from a prior value to a current one — null when the prior
// value is 0 (no baseline to compare against), so the UI can show "New"
// instead of a misleading infinite/undefined percentage.
export function percentChange(current, prior) {
  if (!prior) return null;
  return Math.round(((current - prior) / Math.abs(prior)) * 1000) / 10;
}

async function getRevenueTotals(accountingPeriod) {
  const snapshotsResult = await getDocs(query(collection(db, SNAPSHOTS), where("accountingPeriod", "==", accountingPeriod)));
  let netServiceRevenue = 0;
  let grossBilling = 0;
  let whtRecognizedExpected = 0;
  let gstOutput = 0;
  let invoiceCount = 0;
  const clientCodes = new Set();
  const allLines = [];

  activeSnapshotDocs(snapshotsResult).forEach((docSnap) => {
    const data = docSnap.data();
    const lines = data.lines || [];
    netServiceRevenue += sumRevenueLines(lines);
    grossBilling += sumGrossBilling(lines);
    whtRecognizedExpected += Number(data.totals?.whtAmount || 0);
    gstOutput += Number(data.totals?.gstAmount || 0);
    invoiceCount += 1;
    clientCodes.add(data.clientCode);
    allLines.push(...lines);
  });

  return {
    netServiceRevenue: Math.round(netServiceRevenue),
    grossBilling: Math.round(grossBilling),
    whtRecognizedExpected: Math.round(whtRecognizedExpected),
    gstOutput: Math.round(gstOutput),
    invoiceCount,
    clientCount: clientCodes.size,
    revenueByCategory: sumLinesByCategory(allLines)
  };
}

// Lightweight per-period bottom line — skips the per-line category breakdown
// and client profitability that the full report needs. Used both by the
// prior-month comparison strip and by the Unappropriated P&L history table,
// which needs many periods at once and would otherwise pay for that overhead
// once per month shown.
export async function getMonthlyNetProfit(accountingPeriod) {
  const [revenue, operatingExpenses, directCost] = await Promise.all([
    getRevenueTotals(accountingPeriod),
    getOperatingExpensesForPeriod(accountingPeriod),
    getDirectCostVarianceForPeriod(accountingPeriod)
  ]);
  const grossProfit = calculateGrossProfit({ netServiceRevenue: revenue.netServiceRevenue, directCostVariance: directCost.total });
  const operatingProfit = calculateOperatingProfit({ grossProfit, operatingExpenses: operatingExpenses.total });
  return {
    accountingPeriod,
    invoiceCount: revenue.invoiceCount,
    netServiceRevenue: revenue.netServiceRevenue,
    operatingExpenses: operatingExpenses.total,
    directCost: directCost.total,
    grossProfit,
    operatingProfit
  };
}

async function getPriorTotals(accountingPeriod) {
  const result = await getMonthlyNetProfit(accountingPeriod);
  return { netServiceRevenue: result.netServiceRevenue, grossProfit: result.grossProfit, operatingProfit: result.operatingProfit };
}

export async function getMonthlyPnl(accountingPeriod) {
  const [revenue, operatingExpenses, directCost, topClients, prior, gstPayable] = await Promise.all([
    getRevenueTotals(accountingPeriod),
    getOperatingExpensesForPeriod(accountingPeriod),
    getDirectCostVarianceForPeriod(accountingPeriod),
    getClientProfitabilityForPeriod(accountingPeriod),
    getPriorTotals(previousPeriod(accountingPeriod)),
    getGstPayableSummary(accountingPeriod)
  ]);

  const grossProfit = calculateGrossProfit({ netServiceRevenue: revenue.netServiceRevenue, directCostVariance: directCost.total });
  const operatingProfit = calculateOperatingProfit({ grossProfit, operatingExpenses: operatingExpenses.total });
  const marginPercent = calculateMarginPercent({ grossProfit, netServiceRevenue: revenue.netServiceRevenue });

  return {
    accountingPeriod,
    previousPeriod: previousPeriod(accountingPeriod),
    invoiceCount: revenue.invoiceCount,
    clientCount: revenue.clientCount,
    grossBilling: revenue.grossBilling,
    netServiceRevenue: revenue.netServiceRevenue,
    revenueByCategory: revenue.revenueByCategory,
    whtRecognizedExpected: revenue.whtRecognizedExpected,
    gstOutput: revenue.gstOutput,
    grossProfit,
    directCost,
    operatingExpenses,
    gstPayable,
    operatingProfit,
    marginPercent,
    topClients: topClients.slice(0, 8),
    priorMonth: {
      netServiceRevenueChange: percentChange(revenue.netServiceRevenue, prior.netServiceRevenue),
      grossProfitChange: percentChange(grossProfit, prior.grossProfit),
      operatingProfitChange: percentChange(operatingProfit, prior.operatingProfit)
    }
  };
}
